import { createFileRoute, Link } from "@tanstack/react-router";
import { loadGlossaryList } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export const Route = createFileRoute("/glossary/")({
  loader: () => loadGlossaryList(),
  head: () => ({
    meta: seo({
      title: "Glossary",
      description:
        "Identity security and access governance glossary. Definitions for IAM, IGA, RBAC, ABAC, and more.",
      url: "/glossary",
    }),
    links: seoLinks({ url: "/glossary" }),
  }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  const { groups, featured, latest } = Route.useLoaderData();

  return (
    <>
      {/* Featured post + latest articles section */}
      <div className="mt-10 px-4 md:px-8">
        <div className="mx-auto max-w-screen-xl">
          {/* Featured post hero */}
          {featured && (
            <>
              <span className="chip mb-8 bg-teal-dark/10 text-teal-dark lg:mb-6">
                glossary
              </span>
              <div className="relative grid items-end gap-4 lg:grid-cols-2">
                <div className="block h-full overflow-hidden lg:order-last">
                  {featured.featured_image && (
                    <img
                      src={featured.featured_image}
                      alt={featured.title}
                      className="rounded-[10px] lg:h-full lg:object-cover lg:object-center"
                    />
                  )}
                </div>
                <div className="lg:pr-12">
                  <div className="max-w-lg border-b border-[#DCDADA] pb-7">
                    <span className="inline-block font-body text-xs font-semibold uppercase tracking-widest text-charcoal">
                      Featured
                    </span>
                    <h1 className="mt-2 font-display text-3xl font-bold leading-12 text-charcoal md:text-4xl md:leading-12">
                      {featured.title}
                    </h1>
                    <p className="mt-4 font-body text-base text-gray-80">
                      {featured.description}
                    </p>
                    <Link
                      to="/glossary/$slug"
                      params={{ slug: featured.slug }}
                      className="group relative mt-4 inline-block font-body text-base leading-none text-teal-dark lg:mt-6"
                    >
                      <div className="relative inline-flex items-center group-hover:space-x-2">
                        <span>Read more</span>
                        <svg
                          className="h-[9px] w-0 transition-all duration-400 group-hover:w-5"
                          width="19"
                          height="9"
                          viewBox="0 0 19 9"
                          fill="none"
                        >
                          <path
                            d="M18.3536 4.85355C18.5488 4.65829 18.5488 4.34171 18.3536 4.14645L15.1716 0.964467C14.9763 0.769205 14.6597 0.769205 14.4645 0.964467C14.2692 1.15973 14.2692 1.47631 14.4645 1.67157L17.2929 4.5L14.4645 7.32843C14.2692 7.52369 14.2692 7.84027 14.4645 8.03554C14.6597 8.2308 14.9763 8.2308 15.1716 8.03554L18.3536 4.85355ZM0 5L9 5L9 4L0 4L0 5ZM9 5L18 5L18 4L9 4L9 5Z"
                            fill="#00343a"
                          />
                        </svg>
                      </div>
                      <span className="absolute -bottom-0.5 left-0 h-px w-full bg-teal-dark" />
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Latest articles */}
          {latest && latest.length > 0 && (
            <div className="pb-12 pt-9 lg:pt-14">
              <div className="inline-flex items-center rounded bg-teal-dark/10">
                <svg className="h-8 w-8 flex-none" width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="5" fill="white" />
                  <rect width="32" height="32" rx="5" fill="#00343a" fillOpacity="0.15" />
                  <path d="M21.7578 5.27539H9.89006C8.80219 5.27539 7.91211 6.22481 7.91211 7.38521V24.2637C7.91211 25.4241 8.80219 26.3735 9.89006 26.3735H21.7578C22.8456 26.3735 23.7357 25.4241 23.7357 24.2637V7.38521C23.7357 6.22481 22.8456 5.27539 21.7578 5.27539ZM21.7578 24.2637H9.89006V7.38521H14.8349V14.7696L17.3074 13.1872L19.7798 14.7696V7.38521H21.7578V24.2637Z" fill="#00343a" />
                  <path d="M20.5357 16.13H11.2087V17.8024H20.5357V16.13Z" fill="#00343a" />
                  <path d="M20.5357 18.7673H11.2087V20.4397H20.5357V18.7673Z" fill="#00343a" />
                  <path d="M17.8984 21.4045H11.2087V23.077H17.8984V21.4045Z" fill="#00343a" />
                </svg>
                <div className="px-3 py-1 font-body text-sm font-semibold text-charcoal">
                  Latest articles
                </div>
              </div>
              <div className="mt-4 grid gap-5 md:grid-cols-2 lg:mt-4 lg:grid-cols-3">
                {latest.map(
                  (article: { slug: string; title: string; description: string }) => (
                    <div
                      key={article.slug}
                      className="group rounded-lg bg-[#EBEBE9] p-6 transition-all hover:bg-white hover:shadow-card"
                    >
                      <h3 className="font-body text-base font-bold text-charcoal">{article.title}</h3>
                      <p className="mt-2.5 font-body text-sm text-gray-60">
                        {article.description}
                      </p>
                      <Link
                        to="/glossary/$slug"
                        params={{ slug: article.slug }}
                        className="group/link relative mt-4 inline-block font-body text-base leading-none text-gray-60 lg:mt-6"
                      >
                        <div className="relative inline-flex items-center group-hover/link:space-x-2">
                          <span>Read more</span>
                          <svg
                            className="h-[9px] w-0 transition-all duration-400 group-hover/link:w-5"
                            width="19"
                            height="9"
                            viewBox="0 0 19 9"
                            fill="none"
                          >
                            <path
                              d="M18.3536 4.85355C18.5488 4.65829 18.5488 4.34171 18.3536 4.14645L15.1716 0.964467C14.9763 0.769205 14.6597 0.769205 14.4645 0.964467C14.2692 1.15973 14.2692 1.47631 14.4645 1.67157L17.2929 4.5L14.4645 7.32843C14.2692 7.52369 14.2692 7.84027 14.4645 8.03554C14.6597 8.2308 14.9763 8.2308 15.1716 8.03554L18.3536 4.85355ZM0 5L9 5L9 4L0 4L0 5ZM9 5L18 5L18 4L9 4L9 5Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                        <span className="absolute -bottom-0.5 left-0 h-px w-full bg-gray-60" />
                      </Link>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alphabet jump bar */}
      <div className="sticky top-12 z-10 bg-cream pl-4 md:top-20 md:px-8">
        <div className="mx-auto max-w-screen-xl pt-9">
          <h2 className="font-body text-xs font-semibold uppercase tracking-widest text-teal-dark">
            Jump to section
          </h2>
          <div className="overflow-x-auto pb-4">
            <ul className="mr-6 mt-3 inline-flex rounded-lg bg-[#161415] text-lg font-semibold text-white">
              {ALPHABET.map((letter) => {
                const hasEntries = !!groups[letter];
                return hasEntries ? (
                  <li key={letter}>
                    <a
                      href={`#section-${letter.toLowerCase()}`}
                      className="flex h-9 w-9 items-center justify-center uppercase hover:underline"
                    >
                      {letter}
                    </a>
                  </li>
                ) : (
                  <li key={letter}>
                    <span className="flex h-9 w-9 items-center justify-center uppercase text-gray-60">
                      {letter}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* Entries grouped by letter */}
      <div className="bg-cream">
        <div className="mx-auto max-w-screen-xl px-4 pb-14 md:px-8 md:pb-28">
          <div className="pt-5 md:pt-9">
            {ALPHABET.map((letter) => {
              const entries = groups[letter];
              if (!entries || entries.length === 0) return null;
              return (
                <section
                  key={letter}
                  id={`section-${letter.toLowerCase()}`}
                  className="flex scroll-mt-44 flex-col gap-3 border-b border-[#DCDADA] py-4 text-gray-60 hover:text-gray-80 md:scroll-mt-56 md:flex-row md:py-7 xl:gap-12"
                >
                  <h2 className="px-3 font-display text-xl font-bold text-charcoal md:py-1">
                    {letter}
                  </h2>
                  <ul className="grid flex-1 gap-2 text-base leading-tight lg:grid-cols-2 lg:gap-x-0">
                    {entries.map((entry) => (
                      <li key={entry.slug} className="mx-3 lg:pr-5">
                        <div className="-mx-3 rounded-md hover:bg-teal-dark/10">
                          <Link
                            to="/glossary/$slug"
                            params={{ slug: entry.slug }}
                            className="inline-block px-3 py-2"
                          >
                            {entry.title}
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
